/**
 * 🔍 车辆数据访问器
 *
 * 提供统一的车辆数据访问接口
 * 支持数据查询、过滤、排序和统计
 */

import { VehicleData, RSMDataAdapter } from './RSMDataAdapter';

// 为了兼容性，保留ExtendedRSMParticipant类型别名
export type ExtendedRSMParticipant = VehicleData;

// === 数据查询条件定义 ===

/**
 * 车辆查询条件
 */
export interface VehicleQueryCondition {
  /** 车辆ID列表 */
  ids?: string[];
  /** 车辆类型过滤 */
  types?: Array<0 | 1 | 2 | 3 | 4 | 5>;
  /** 位置区域过滤 */
  bounds?: {
    minLat: number;
    maxLat: number;
    minLon: number;
    maxLon: number;
  };
  /** 速度范围过滤 */
  speedRange?: {
    min: number;
    max: number;
  };
  /** 距离过滤（基于指定中心点） */
  distance?: {
    centerLat: number;
    centerLon: number;
    radius: number; // 米
  };
  /** 时间范围过滤 */
  timeRange?: {
    start: number;
    end: number;
  };
  /** 数据源过滤 */
  sources?: number[];
  /** 文本搜索（车辆ID） */
  search?: string;
}

/**
 * 排序配置
 */
export interface VehicleSortConfig {
  /** 排序字段 */
  field: 'vehicleId' | 'ptcTimestamp' | 'speed' | 'distance' | 'ptcType';
  /** 排序方向 */
  order: 'asc' | 'desc';
}

/**
 * 分页配置
 */
export interface VehiclePaginationConfig {
  /** 页码（从1开始） */
  page: number;
  /** 每页数量 */
  pageSize: number;
}

/**
 * 查询结果
 */
export interface VehicleQueryResult {
  /** 车辆数据列表 */
  vehicles: VehicleData[];
  /** 总数量 */
  total: number;
  /** 分页信息 */
  pagination?: {
    page: number;
    pageSize: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

/**
 * 统计信息
 */
export interface VehicleStatistics {
  /** 总车辆数 */
  total: number;
  /** 按类型分组统计 */
  byType: Record<string, number>;
  /** 按数据源分组统计 */
  bySource: Record<string, number>;
  /** 速度统计 */
  speed: {
    min: number;
    max: number;
    average: number;
    median: number;
  };
  /** 位置边界 */
  bounds: {
    minLat: number;
    maxLat: number;
    minLon: number;
    maxLon: number;
  };
  /** 更新时间范围 */
  timeRange: {
    earliest: number;
    latest: number;
  };
}

// === 车辆数据访问器类 ===

/**
 * 车辆数据访问器
 */
export class VehicleDataAccessor {
  private vehicleData: Map<string, VehicleData> = new Map();
  private indexedData: {
    byType: Map<string, Set<string>>;
    bySource: Map<string, Set<string>>;
  } = {
    byType: new Map(),
    bySource: new Map(),
  };

  constructor(initialData: VehicleData[] = []) {
    this.setVehicleData(initialData);
  }

  /**
   * 设置车辆数据
   */
  setVehicleData(vehicles: VehicleData[]): void {
    this.vehicleData.clear();
    this.clearIndexes();

    vehicles.forEach(vehicle => {
      this.addVehicle(vehicle);
    });
  }

  /**
   * 添加车辆数据
   */
  addVehicle(vehicle: VehicleData): void {
    this.vehicleData.set(vehicle.runtime.vehicleId, vehicle);
    this.updateIndexes(vehicle);
  }

  /**
   * 更新车辆数据
   */
  updateVehicle(vehicle: VehicleData): void {
    const existingVehicle = this.vehicleData.get(vehicle.runtime.vehicleId);
    if (existingVehicle) {
      this.removeFromIndexes(existingVehicle);
    }

    this.vehicleData.set(vehicle.runtime.vehicleId, vehicle);
    this.updateIndexes(vehicle);
  }

  /**
   * 移除车辆数据
   */
  removeVehicle(vehicleId: string): boolean {
    const vehicle = this.vehicleData.get(vehicleId);
    if (vehicle) {
      this.vehicleData.delete(vehicleId);
      this.removeFromIndexes(vehicle);
      return true;
    }
    return false;
  }

  /**
   * 批量更新车辆数据
   */
  batchUpdateVehicles(vehicles: VehicleData[]): void {
    vehicles.forEach(vehicle => {
      this.updateVehicle(vehicle);
    });
  }

  /**
   * 清空所有车辆数据
   */
  clear(): void {
    this.vehicleData.clear();
    this.clearIndexes();
  }

  /**
   * 根据ID获取车辆
   */
  getVehicleById(id: string): VehicleData | undefined {
    return this.vehicleData.get(id);
  }

  /**
   * 获取所有车辆数据
   */
  getAllVehicles(): VehicleData[] {
    return Array.from(this.vehicleData.values());
  }

  /**
   * 查询车辆数据
   */
  queryVehicles(
    condition: VehicleQueryCondition = {},
    sort?: VehicleSortConfig,
    pagination?: VehiclePaginationConfig
  ): VehicleQueryResult {
    let vehicles = this.getAllVehicles();

    // 应用查询条件
    vehicles = this.applyQueryCondition(vehicles, condition);

    // 应用排序
    if (sort) {
      vehicles = this.applySorting(vehicles, sort, condition);
    }

    const total = vehicles.length;

    // 应用分页
    if (pagination) {
      const startIndex = (pagination.page - 1) * pagination.pageSize;
      const endIndex = startIndex + pagination.pageSize;
      vehicles = vehicles.slice(startIndex, endIndex);

      const totalPages = Math.ceil(total / pagination.pageSize);

      return {
        vehicles,
        total,
        pagination: {
          page: pagination.page,
          pageSize: pagination.pageSize,
          totalPages,
          hasNext: pagination.page < totalPages,
          hasPrev: pagination.page > 1,
        },
      };
    }

    return {
      vehicles,
      total,
    };
  }

  /**
   * 根据类型获取车辆
   */
  getVehiclesByType(type: string): VehicleData[] {
    const vehicleIds = this.indexedData.byType.get(type);
    if (!vehicleIds) return [];

    return Array.from(vehicleIds)
      .map(id => this.vehicleData.get(id))
      .filter((vehicle): vehicle is VehicleData => vehicle !== undefined);
  }

  /**
   * 根据数据源获取车辆
   */
  getVehiclesBySource(source: string): VehicleData[] {
    const vehicleIds = this.indexedData.bySource.get(source);
    if (!vehicleIds) return [];

    return Array.from(vehicleIds)
      .map(id => this.vehicleData.get(id))
      .filter((vehicle): vehicle is VehicleData => vehicle !== undefined);
  }

  /**
   * 获取指定位置附近的车辆
   */
  getVehiclesNearLocation(
    centerLat: number,
    centerLon: number,
    radiusInMeters: number
  ): VehicleData[] {
    return this.getAllVehicles().filter(vehicle => {
      const distance = this.calculateDistance(
        centerLat,
        centerLon,
        vehicle.latitude,
        vehicle.longitude
      );
      return distance <= radiusInMeters;
    });
  }

  /**
   * 获取指定区域内的车辆
   */
  getVehiclesInBounds(bounds: {
    minLat: number;
    maxLat: number;
    minLon: number;
    maxLon: number;
  }): VehicleData[] {
    return this.getAllVehicles().filter(
      vehicle =>
        vehicle.latitude >= bounds.minLat &&
        vehicle.latitude <= bounds.maxLat &&
        vehicle.longitude >= bounds.minLon &&
        vehicle.longitude <= bounds.maxLon
    );
  }

  /**
   * 搜索车辆（基于车辆ID）
   */
  searchVehicles(searchText: string): VehicleData[] {
    const lowerSearchText = searchText.toLowerCase();

    return this.getAllVehicles().filter(vehicle =>
      vehicle.runtime.vehicleId.toLowerCase().includes(lowerSearchText)
    );
  }

  /**
   * 获取统计信息
   */
  getStatistics(): VehicleStatistics {
    const vehicles = this.getAllVehicles();

    if (vehicles.length === 0) {
      return {
        total: 0,
        byType: {},
        bySource: {},
        speed: { min: 0, max: 0, average: 0, median: 0 },
        bounds: { minLat: 0, maxLat: 0, minLon: 0, maxLon: 0 },
        timeRange: { earliest: 0, latest: 0 },
      };
    }

    // 按类型统计
    const byType: Record<string, number> = {};
    vehicles.forEach(vehicle => {
      const type = RSMDataAdapter.mapPtcTypeToString(vehicle.ptcType);
      byType[type] = (byType[type] || 0) + 1;
    });

    // 按数据源统计
    const bySource: Record<string, number> = {};
    vehicles.forEach(vehicle => {
      const source = vehicle.sourceType.toString();
      bySource[source] = (bySource[source] || 0) + 1;
    });

    // 速度统计
    const speeds = vehicles.map(v => v.speed);
    speeds.sort((a, b) => a - b);
    const speedStats = {
      min: Math.min(...speeds),
      max: Math.max(...speeds),
      average: speeds.reduce((sum, speed) => sum + speed, 0) / speeds.length,
      median: speeds[Math.floor(speeds.length / 2)],
    };

    // 位置边界
    const bounds = {
      minLat: Math.min(...vehicles.map(v => v.latitude)),
      maxLat: Math.max(...vehicles.map(v => v.latitude)),
      minLon: Math.min(...vehicles.map(v => v.longitude)),
      maxLon: Math.max(...vehicles.map(v => v.longitude)),
    };

    // 时间范围
    const timestamps = vehicles.map(v => v.runtime.messageTimestamp);
    const timeRange = {
      earliest: Math.min(...timestamps),
      latest: Math.max(...timestamps),
    };

    return {
      total: vehicles.length,
      byType,
      bySource,
      speed: speedStats,
      bounds,
      timeRange,
    };
  }

  /**
   * 获取车辆数量
   */
  getVehicleCount(): number {
    return this.vehicleData.size;
  }

  /**
   * 检查车辆是否存在
   */
  hasVehicle(vehicleId: string): boolean {
    return this.vehicleData.has(vehicleId);
  }

  /**
   * 获取所有车辆ID
   */
  getAllVehicleIds(): string[] {
    return Array.from(this.vehicleData.keys());
  }

  /**
   * 获取所有车辆类型
   */
  getAllVehicleTypes(): string[] {
    return Array.from(this.indexedData.byType.keys());
  }

  /**
   * 获取所有数据源
   */
  getAllDataSources(): string[] {
    return Array.from(this.indexedData.bySource.keys());
  }

  /**
   * 应用查询条件
   */
  private applyQueryCondition(
    vehicles: VehicleData[],
    condition: VehicleQueryCondition
  ): VehicleData[] {
    let filtered = vehicles;

    // ID过滤
    if (condition.ids && condition.ids.length > 0) {
      const idSet = new Set(condition.ids);
      filtered = filtered.filter(v => idSet.has(v.runtime.vehicleId));
    }

    // 类型过滤
    if (condition.types && condition.types.length > 0) {
      const typeSet = new Set(condition.types);
      filtered = filtered.filter(v => typeSet.has(v.ptcType));
    }

    // 数据源过滤
    if (condition.sources && condition.sources.length > 0) {
      const sourceSet = new Set(condition.sources);
      filtered = filtered.filter(v => sourceSet.has(v.sourceType));
    }

    // 位置区域过滤
    if (condition.bounds) {
      const { minLat, maxLat, minLon, maxLon } = condition.bounds;
      filtered = filtered.filter(
        v =>
          v.latitude >= minLat &&
          v.latitude <= maxLat &&
          v.longitude >= minLon &&
          v.longitude <= maxLon
      );
    }

    // 速度范围过滤
    if (condition.speedRange) {
      const { min, max } = condition.speedRange;
      filtered = filtered.filter(v => v.speed >= min && v.speed <= max);
    }

    // 距离过滤
    if (condition.distance) {
      const { centerLat, centerLon, radius } = condition.distance;
      filtered = filtered.filter(v => {
        const distance = this.calculateDistance(
          centerLat,
          centerLon,
          v.latitude,
          v.longitude
        );
        return distance <= radius;
      });
    }

    // 时间范围过滤
    if (condition.timeRange) {
      const { start, end } = condition.timeRange;
      filtered = filtered.filter(
        v => v.ptcTimestamp >= start && v.ptcTimestamp <= end
      );
    }

    // 文本搜索
    if (condition.search) {
      const searchText = condition.search.toLowerCase();
      filtered = filtered.filter(v =>
        v.runtime.vehicleId.toLowerCase().includes(searchText)
      );
    }

    return filtered;
  }

  /**
   * 应用排序
   */
  private applySorting(
    vehicles: VehicleData[],
    sort: VehicleSortConfig,
    condition: VehicleQueryCondition = {}
  ): VehicleData[] {
    return vehicles.sort((a, b) => {
      let valueA: any;
      let valueB: any;

      switch (sort.field) {
        case 'distance':
          if (condition.distance) {
            valueA = this.calculateDistance(
              condition.distance.centerLat,
              condition.distance.centerLon,
              a.latitude,
              a.longitude
            );
            valueB = this.calculateDistance(
              condition.distance.centerLat,
              condition.distance.centerLon,
              b.latitude,
              b.longitude
            );
          } else {
            return 0;
          }
          break;
        case 'speed':
          valueA = a.speed;
          valueB = b.speed;
          break;
        case 'ptcType':
          valueA = a.ptcType;
          valueB = b.ptcType;
          break;
        case 'ptcTimestamp':
          valueA = a.ptcTimestamp;
          valueB = b.ptcTimestamp;
          break;
        case 'vehicleId':
        default:
          valueA = a.runtime.vehicleId;
          valueB = b.runtime.vehicleId;
          break;
      }

      // 处理不同类型的比较
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        valueA = valueA.toLowerCase();
        valueB = valueB.toLowerCase();
      }

      let comparison = 0;
      if (valueA < valueB) {
        comparison = -1;
      } else if (valueA > valueB) {
        comparison = 1;
      }

      return sort.order === 'desc' ? -comparison : comparison;
    });
  }

  /**
   * 更新索引
   */
  private updateIndexes(vehicle: VehicleData): void {
    // 类型索引
    const typeKey = RSMDataAdapter.mapPtcTypeToString(vehicle.ptcType);
    if (!this.indexedData.byType.has(typeKey)) {
      this.indexedData.byType.set(typeKey, new Set());
    }
    this.indexedData.byType.get(typeKey)!.add(vehicle.runtime.vehicleId);

    // 数据源索引
    const sourceKey = vehicle.sourceType.toString();
    if (!this.indexedData.bySource.has(sourceKey)) {
      this.indexedData.bySource.set(sourceKey, new Set());
    }
    this.indexedData.bySource.get(sourceKey)!.add(vehicle.runtime.vehicleId);
  }

  /**
   * 从索引中移除
   */
  private removeFromIndexes(vehicle: VehicleData): void {
    // 类型索引
    const typeKey = RSMDataAdapter.mapPtcTypeToString(vehicle.ptcType);
    this.indexedData.byType.get(typeKey)?.delete(vehicle.runtime.vehicleId);
    if (this.indexedData.byType.get(typeKey)?.size === 0) {
      this.indexedData.byType.delete(typeKey);
    }

    // 数据源索引
    const sourceKey = vehicle.sourceType.toString();
    this.indexedData.bySource.get(sourceKey)?.delete(vehicle.runtime.vehicleId);
    if (this.indexedData.bySource.get(sourceKey)?.size === 0) {
      this.indexedData.bySource.delete(sourceKey);
    }
  }

  /**
   * 清空索引
   */
  private clearIndexes(): void {
    this.indexedData.byType.clear();
    this.indexedData.bySource.clear();
  }

  /**
   * 计算两点间距离（米）
   */
  private calculateDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    const R = 6371000; // 地球半径（米）
    const dLat = this.toRadians(lat2 - lat1);
    const dLon = this.toRadians(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(lat1)) *
        Math.cos(this.toRadians(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * 角度转弧度
   */
  private toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }
}

// === 导出工厂函数 ===

/**
 * 创建车辆数据访问器
 */
export function createVehicleDataAccessor(
  initialData: VehicleData[] = []
): VehicleDataAccessor {
  return new VehicleDataAccessor(initialData);
}

// === 默认导出 ===

export default VehicleDataAccessor;
